/**
 * PublicFooter — unified footer rendered by every public page
 * (Landing, Pro, Referrals).
 *
 * Visual and content match what was previously the LandingFooter:
 * brand block, three link columns (Platform / Account / Support), and
 * a legal bar with copyright + policy links.
 *
 * Note: the "Platform" anchor links (#features, #pricing, #for-readers)
 * work on the Landing page directly. On the Pro page, `#features` and
 * `#pricing` happen to exist and scroll to those sections. On the
 * Referrals page these anchors no-op; that's consistent with how the
 * Landing footer behaved when previously copy-pasted. Point them at
 * `/#features` etc. later if cross-page scroll-to-section is wanted.
 *
 * AppLayout hides its own compact legal bar on public pages to avoid
 * a double footer — see `components/layout/AppLayout.tsx`.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Footer,
  FooterInner,
  FooterBrand,
  FooterTagline,
  FooterLinks,
  FooterCol,
  FooterBottom,
  FooterBottomInner,
  FooterStatus,
  FooterSep,
} from './LandingPage.styled';
import { VellumLogo } from '../../../components/brand';
import { useCookieConsent } from '../../../contexts/CookieConsentContext';
import { ReactComponent as GDPRBadge } from '../../../assets/compliance/GDPR-Compliant.svg';
import { ReactComponent as CCPABadge } from '../../../assets/compliance/CCPA-Compliant.svg';
import StatusBadge from '../../../components/layout/StatusBadge';
import Box from '@mui/material/Box';

export function PublicFooter() {
  const { t } = useTranslation();
  const { openPreferences } = useCookieConsent();

  return (
    <Footer>
      <FooterInner>
        <FooterBrand>
          <VellumLogo iconHeight={42} color="#D9D9D9" />
          <FooterTagline>{t('landing.footer.tagline')}</FooterTagline>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 5 }}>
            <GDPRBadge height={50} width={132} />
            <CCPABadge height={50} width={132} />
            <span className="sr-only">{t('landing.footer.complianceAria')}</span>
          </Box>
        </FooterBrand>
        <FooterLinks>
          <FooterCol>
            <strong>{t('landing.footer.platform')}</strong>
            <a href="#features">{t('landing.footer.links.features')}</a>
            <a href="#pricing">{t('landing.footer.links.pricing')}</a>
            <a href="#for-readers">{t('landing.footer.links.forReaders')}</a>
            <Link to="/search">{t('landing.footer.links.discover')}</Link>
            <Link to="/pro">{t('landing.footer.links.pro')}</Link>
            <Link to="/referrals">{t('landing.footer.links.referral')}</Link>
          </FooterCol>
          <FooterCol>
            <strong>{t('landing.footer.account')}</strong>
            <Link to="/register">{t('landing.footer.links.getStarted')}</Link>
            <Link to="/login">{t('landing.footer.links.signIn')}</Link>
            <Link to="/settings">{t('landing.footer.links.settings')}</Link>
            <Link to="/dashboard">{t('landing.footer.links.dashboard')}</Link>
          </FooterCol>
          <FooterCol>
            <strong>{t('landing.footer.support')}</strong>
            <a href="mailto:hello@writevellum.com">hello@writevellum.com</a>
            <a href="https://status.writevellum.com/" target="_blank" rel="noopener noreferrer">
              {t('landing.footer.status', { defaultValue: 'Service status' })}
            </a>
          </FooterCol>
        </FooterLinks>
      </FooterInner>

      <FooterBottom>
        <FooterBottomInner>
          <span>{t('landing.footer.copyright', { year: new Date().getFullYear() })}</span>
        </FooterBottomInner>

        <FooterBottomInner>
          <Link to="/legal/privacy">{t('landing.footer.privacy')}</Link>
          <FooterSep>·</FooterSep>
          <Link to="/legal/terms">{t('landing.footer.terms')}</Link>
          <FooterSep>·</FooterSep>
          <Link to="/legal/cookies">
            {t('landing.footer.cookies', { defaultValue: 'Cookie Policy' })}
          </Link>
          <FooterSep>·</FooterSep>
          <Link to="/legal/dmca">{t('landing.footer.dmca', { defaultValue: 'DMCA' })}</Link>
          <FooterSep>·</FooterSep>
          <Link to="/legal/refunds">
            {t('landing.footer.refunds', { defaultValue: 'Refunds' })}
          </Link>
          <FooterSep>·</FooterSep>
          <Link to="/legal/community-guidelines">
            {t('landing.footer.community', { defaultValue: 'Community Guidelines' })}
          </Link>
          <FooterSep>·</FooterSep>
          <Link to="/legal/csam-policy">
            {t('landing.footer.csam', { defaultValue: 'CSAM Policy' })}
          </Link>
          <FooterSep>·</FooterSep>
          {/* Cookie preferences is a button, not a link: it opens the modal in
              place instead of navigating. Styled as a link via unset defaults
              so it blends into the footer row. */}
          <button
            type="button"
            onClick={openPreferences}
            style={{
              all: 'unset',
              cursor: 'pointer',
              color: 'inherit',
              textDecoration: 'underline',
              textUnderlineOffset: 2,
            }}
          >
            {t('landing.footer.cookiePrefs', { defaultValue: 'Cookie preferences' })}
          </button>
        </FooterBottomInner>

        {/* Live service status badge — same self-rendered component the
            authenticated app footer uses. The previous BetterStack iframe
            was cross-origin which blocked the parent from styling it; this
            polls /api/status (which proxies BetterStack) and animates a
            coloured dot driven off the response. */}
        <FooterStatus>
          <StatusBadge />
        </FooterStatus>
      </FooterBottom>
    </Footer>
  );
}

export default PublicFooter;
